import { datadogRum } from '@datadog/browser-rum';

const TOAST_STYLES = `
  * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
  #toast-stack {
    position: fixed;
    bottom: 40px;
    right: 8px;
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    z-index: 2147483646;
    max-width: 360px;
    width: 360px;
    pointer-events: none;
  }
  .toast {
    background: #1e1b4b;
    color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0,0,0,.45);
    overflow: hidden;
    pointer-events: all;
    animation: slide-in .2s ease;
  }
  @keyframes slide-in {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .toast-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px 6px;
    background: #dc2626;
    border-radius: 10px 10px 0 0;
  }
  .toast-header .icon { font-size: 13px; flex-shrink: 0; }
  .toast-header .title { font-size: 12px; font-weight: 700; flex: 1; letter-spacing: .3px; }
  .toast-header .dismiss {
    background: none; border: none; color: rgba(255,255,255,.8);
    font-size: 15px; cursor: pointer; padding: 0 2px; line-height: 1;
    flex-shrink: 0;
  }
  .toast-header .dismiss:hover { color: #fff; }
  .toast-body { padding: 8px 10px 10px; }
  .error-type {
    font-size: 10px;
    color: #a5b4fc;
    margin-bottom: 3px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .5px;
  }
  .error-msg {
    font-size: 12px;
    color: #e0e7ff;
    line-height: 1.45;
    word-break: break-word;
    margin-bottom: 8px;
  }
  .dd-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(255,255,255,.12);
    color: #c7d2fe;
    text-decoration: none;
    border-radius: 6px;
    padding: 4px 9px;
    font-size: 11px;
    font-weight: 600;
    transition: background .15s;
  }
  .dd-link:hover { background: rgba(255,255,255,.22); color: #fff; }
  .dd-hint {
    font-size: 11px;
    color: rgba(255,255,255,.5);
    margin-bottom: 8px;
  }
`;

const MAX_MSG_LENGTH = 120;

let _site = null;
let _root = null;
let _stack = null;

function buildErrorTrackingUrl(errorTime) {
  if (!_site) return null;
  const ctx = datadogRum.getInternalContext?.();
  const sessionId = ctx?.session_id;
  const ts = new Date(errorTime).getTime();
  const from = ts; // exact moment the error was captured
  const to = ts + 5 * 60 * 1000; // 5 min after

  const queryParts = ['service:overhaul-frontend -env:prod'];
  if (sessionId) queryParts.push(`@session.id:${sessionId}`);

  const params = new URLSearchParams({
    query: queryParts.join(' '),
    issue_states: '',
    source: 'all',
    from_ts: String(from),
    to_ts: String(to),
    live: 'false',
  });

  return `https://${_site}/error-tracking?${params}`;
}

export function showErrorToast({ message, errorType, source, t }) {
  if (!_root || !_stack || window.isDDErrorsHidden) return;

  const truncated = message.length > MAX_MSG_LENGTH
    ? message.slice(0, MAX_MSG_LENGTH) + '…'
    : message;

  renderToast({ truncated, errorType, source, errorTrackingUrl: buildErrorTrackingUrl(t) });
}

function esc(str) {
  const d = document.createElement('span');
  d.textContent = str;
  return d.innerHTML;
}

function renderToast({ truncated, errorType, source, errorTrackingUrl }) {
  if (!_root || !_stack) return;

  const badge = [errorType, source].filter(Boolean).join(' · ');

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.innerHTML = `
    <div class="toast-header">
      <span class="icon">🚨</span>
      <span class="title">JS Error Captured</span>
      <button class="dismiss" aria-label="Dismiss">✕</button>
    </div>
    <div class="toast-body">
      ${badge ? `<div class="error-type">${esc(badge)}</div>` : ''}
      <div class="error-msg">${esc(truncated)}</div>
      <div class="dd-hint">⏱ Error will appear in DD in ~30s</div>
      ${errorTrackingUrl ? `<a class="dd-link" href="${esc(errorTrackingUrl)}" target="_blank" rel="noopener">Error Tracking ↗</a>` : ''}
    </div>
  `;

  toast.querySelector('.dismiss').addEventListener('click', () => toast.remove());

  _stack.appendChild(toast);
}

export function initErrorToast(DATADOG_SITE) {
  _site = DATADOG_SITE;

  const host = document.createElement('div');
  host.id = '__dd-error-toast-host';
  Object.assign(host.style, { all: 'initial', position: 'fixed', zIndex: 2147483646 });
  document.body.appendChild(host);

  _root = host.attachShadow({ mode: 'open' });

  const style = document.createElement('style');
  style.textContent = TOAST_STYLES;
  _root.appendChild(style);

  _stack = document.createElement('div');
  _stack.id = 'toast-stack';
  _root.appendChild(_stack);
}
